import { useEffect } from 'react';

const SITE_NAME = 'Timeless Auto Imports Limited';
const SITE_URL = 'https://timelessautoimportslimited.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_luxury-imports-3/artifacts/8azizvqn_WhatsApp%20Image%202026-06-19%20at%204.00.36%20PM.jpeg';

/** Upsert a <meta name="..."> or <meta property="..."> tag in <head>. */
function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Upsert a <link rel="..."> tag in <head>. */
function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Upsert a JSON-LD <script> block by unique data-key attribute. Returns cleanup. */
function setJsonLd(key, obj) {
  if (!obj) return () => {};
  const sel = `script[type="application/ld+json"][data-key="${key}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-key', key);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
  return () => {
    const node = document.head.querySelector(sel);
    if (node) node.remove();
  };
}

/**
 * Set per-page SEO tags (title, description, canonical, OG, Twitter) and
 * optional JSON-LD structured data. Restores previous title on unmount only
 * if the caller cared (usually you don't — the next page overrides it).
 */
export default function useSEO({ title, description, path, image, type = 'website', jsonLd, jsonLdKey = 'page-jsonld' } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Don't Just Dream it, Drive it.`;
    const desc = description || "Jamaica's one-stop automotive shop — Japanese imports, mechanical & hybrid service, body work, parts, and accessories. Lot 36 Bustamante Highway, Four Path, Clarendon.";
    const url = path ? `${SITE_URL}${path.startsWith('/') ? path : '/' + path}` : SITE_URL + (typeof window !== 'undefined' ? window.location.pathname : '/');
    const img = image || DEFAULT_IMAGE;

    document.title = fullTitle;
    setMeta('name', 'description', desc);
    setLink('canonical', url);

    // Open Graph
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', img);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:site_name', SITE_NAME);

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', desc);
    setMeta('name', 'twitter:image', img);

    // Optional JSON-LD
    const cleanupLd = jsonLd ? setJsonLd(jsonLdKey, jsonLd) : () => {};

    return () => {
      cleanupLd();
    };
  }, [title, description, path, image, type, JSON.stringify(jsonLd), jsonLdKey]);
}

/** Convenience helpers for common structured-data shapes. */
export function breadcrumbLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url.startsWith('http') ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
}

export function vehicleLd(v) {
  const name = `${v.year} ${v.make} ${v.model}`;
  const price = v.status === 'on_sale' && v.sale_price ? v.sale_price : v.price;
  const isSold = v.sold || v.status === 'sold';
  return {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name,
    description: v.description || `${name} available at ${SITE_NAME}. Stock #${v.stock}.`,
    brand: { '@type': 'Brand', name: v.make },
    model: v.model,
    vehicleModelDate: String(v.year),
    modelDate: String(v.year),
    color: v.color || undefined,
    fuelType: v.fuel || undefined,
    vehicleTransmission: v.transmission || undefined,
    driveWheelConfiguration: v.drive || undefined,
    bodyType: v.body || undefined,
    mileageFromOdometer: v.mileage ? { '@type': 'QuantitativeValue', value: v.mileage, unitCode: 'KMT' } : undefined,
    image: (v.images || [])[0] || DEFAULT_IMAGE,
    sku: v.stock,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'JMD',
      price: price,
      availability: isSold ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      url: `${SITE_URL}/vehicle/${v.id}`,
      seller: { '@type': 'AutoDealer', name: SITE_NAME },
    },
  };
}

export function productLd(p) {
  const price = p.status === 'on_sale' && p.sale_price ? p.sale_price : p.price;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    description: p.description || `${p.name} — genuine and aftermarket auto parts at ${SITE_NAME}.`,
    image: (p.images || [])[0] || DEFAULT_IMAGE,
    sku: p.sku,
    category: p.category,
    brand: p.brand ? { '@type': 'Brand', name: p.brand } : undefined,
    offers: (price > 0) ? {
      '@type': 'Offer',
      priceCurrency: (p.currency || 'JMD').toUpperCase(),
      price,
      availability: p.in_stock === false ? 'https://schema.org/PreOrder' : 'https://schema.org/InStock',
      url: `${SITE_URL}/parts/${p.id}`,
      seller: { '@type': 'Organization', name: SITE_NAME },
    } : undefined,
  };
}

export function faqLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(it => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  };
}
